import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, MoreVertical, AlertTriangle, FolderPlus, Search, Upload } from 'lucide-react';
import { useStore } from '@tanstack/react-store';
import { useS3Upload } from '../../../hooks/useCommon';
import { selectedLanguageStore, setSelectedLanguage } from '../../../stores/selectedLanguage';
import { useListLanguages } from '../../../hooks/useLanguages';
import { useGetInterestSubCategories, useCreateInterestSubCategory, useUpdateInterestSubCategory, useUpdateInterestSubCategoryStatus, useDeleteInterestSubCategory } from '../../../hooks/useInterest';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export default function FAQsPage() {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const { cat_id } = useParams()
    const [languages, setLanguages] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const queryClient = useQueryClient();
    const uploadMutation = useS3Upload();
    const createSubCategoryMutation = useCreateInterestSubCategory()
    const deleteSubCategoryMutation = useDeleteInterestSubCategory()
    const updateSubCategoryMutation = useUpdateInterestSubCategory()
    const updateStatusMutation = useUpdateInterestSubCategoryStatus()
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(null);
    const [showAddSubCategoryModal, setShowAddSubCategoryModal] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [subCategoryImage, setSubCategoryImage] = useState(null);
    const [newTitle, setNewTitle] = useState('');
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    const [showEditSubCategoryModal, setShowEditSubCategoryModal] = useState(false);
    const [deletingSubCategory, setDeletingSubCategory] = useState(null);
    const [showDeleteSubCategoryConfirmation, setShowDeleteSubCategoryConfirmation] = useState(false);
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const { data: subCategoriesResponse, isSuccess: isSuccessSubCategories } = useGetInterestSubCategories(`?category_id=${cat_id}&language=${selectedLanguage}&search_key=${searchTerm}`)
    const { REACT_APP_IMG_URL } = process.env
    const filePath = "interest"

    // Pagination functions
    let totalPages = Math.ceil(subCategories?.filter(sc => sc?.title?.toLowerCase().includes(searchTerm?.toLowerCase()))?.length / rowsPerPage)
    totalPages = totalPages == 0 ? 1 : totalPages

    const toggleDropdown = (id) => {
        setShowDropdown(showDropdown === id ? null : id);
    };

    const handleEdit = async () => {
        if (editingSubCategory) {
            if (editingSubCategory?.title?.trim() == "") {
                toast.error("Title is not allowed to empty")
                return false
            }
            let subCategoryData = {
                title: editingSubCategory?.title,
                image_link: editingSubCategory?.image_link,
                language: selectedLanguage,
                _id: editingSubCategory?._id
            }
            if (subCategoryImage) {
                const imageResponse = await UploadToS3()
                if (!imageResponse) {
                    toast.success('Please Select a Image to Proceed');
                    return;
                }
                subCategoryData.image_link = `${imageResponse?.Key}`
            }
            updateSubCategoryMutation.mutate(subCategoryData, {
                onSuccess: () => {
                    toast.success("Sub Category Updated Successfully")
                    queryClient.invalidateQueries({ queryKey: ['interest_subcategories'] });
                    setEditingSubCategory(null);
                    setImagePreview(null)
                    setSubCategoryImage(null)
                    setShowEditSubCategoryModal(false);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const handleDelete = () => {
        if (deletingSubCategory) {
            deleteSubCategoryMutation.mutate(
                deletingSubCategory?._id, {
                onSuccess: () => {
                    toast.success("Sub Category has been deleted successfully")
                    queryClient.invalidateQueries({ queryKey: ['interest_subcategories'] });
                    setShowDeleteSubCategoryConfirmation(false);
                    setDeletingSubCategory(null);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const openEditSubCategoryModal = (subCategory) => {
        setEditingSubCategory({ ...subCategory });
        setImagePreview(`${REACT_APP_IMG_URL}/${subCategory?.image_link}`)
        setShowEditSubCategoryModal(true);
        setShowDropdown(null);
    };

    const openDeleteSubCategoryConfirmation = (subCategory) => {
        setDeletingSubCategory(subCategory);
        setShowDeleteSubCategoryConfirmation(true);
        setShowDropdown(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSubCategoryImage(file); // store file
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
        }
    };

    const UploadToS3 = async () => {
        if (subCategoryImage) {
            let response = await uploadMutation.mutateAsync({
                file: subCategoryImage,
                filePath
            })
            return response
        } else {
            toast.error("File not found")
            return null
        }
    }

    const handleAdd = async () => {
        const imageResponse = await UploadToS3()
        if (!imageResponse) {
            toast.success('Please Select a Image to Proceed');
            return;
        }
        if (newTitle == "") {
            toast.error("Title is not allowed to empty")
            return false
        }
        createSubCategoryMutation.mutate({
            title: newTitle,
            image_link: `${imageResponse?.Key}`,
            category_id: cat_id,
            language: selectedLanguage
        }, {
            onSuccess: () => {
                toast.success("New Sub Category added Successfully")
                queryClient.invalidateQueries({ queryKey: ['interest_subcategories'] });
                setNewTitle('');
                setImagePreview(null)
                setSubCategoryImage(null)
                setShowAddSubCategoryModal(false)
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Question status updated.")
                queryClient.invalidateQueries({ queryKey: ['interest_subcategories'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    useEffect(() => {
        if (isSuccessSubCategories) {
            setSubCategories(subCategoriesResponse?.data?.data?.result)
        }
    }, [subCategoriesResponse, isSuccessSubCategories])

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    return (
        <div className="flex h-screen bg-white text-gray-800">
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="border-b border-gray-200 p-4 flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Dashboard</span>
                    <ChevronRight size={14} className="text-gray-400" />
                    <span>Sub-Categories</span>
                </div>
                {/* Content */}
                <div className="p-6 flex justify-between">
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold mb-2">Interest's</h1>
                        <p className="text-gray-500">Manage Interest's for users to see on your app and the website.</p>
                    </div>
                    <div className="mb-6 mt-4">
                        <button
                            onClick={() => setShowAddSubCategoryModal(true)}
                            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 flex items-center gap-2">
                            <FolderPlus size={16} />
                            Add New Sub Category
                        </button>
                    </div>
                </div>
                <div className="mb-6 flex space-x-2">
                    {languages.map(language => (
                        <button
                            key={language?.short_code}
                            className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-600'}`}
                            onClick={() => setSelectedLanguage(language?.short_code)}>
                            {language?.name}
                        </button>
                    ))}
                </div>
                {/* Search */}
                <div className="mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name"
                            className="w-full p-2 pl-10 border border-gray-300 rounded"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                    </div>
                </div>
                {/* Sub Categories Table */}
                <div className="border border-gray-200 rounded mb-4">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-3 text-left w-10">S.No</th>
                                <th className="px-4 py-3 text-left">Image</th>
                                <th className="px-4 py-3 text-left">Title</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subCategories.map((subCategory, i) => {
                                return (
                                    <tr key={subCategory?._id} className="border-b border-gray-200">
                                        <td className="px-4 py-3">{i + 1}</td>
                                        <td className="p-4">
                                            <img
                                                className='w-10 h-10 border rounded cursor-pointer'
                                                src={`${REACT_APP_IMG_URL}/${subCategory?.image_link}`}
                                            />
                                        </td>
                                        <td
                                            className="px-4 py-3 flex items-center cursor-pointer">
                                            {subCategory?.title}
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <button
                                                onClick={() => toggleStatus(subCategory?._id, subCategory?.status)}
                                                className="focus:outline-none">
                                                <div className={`w-12 h-6 rounded-full flex items-center ${subCategory?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                    <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${subCategory.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                </div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="relative inline-block">
                                                <button
                                                    onClick={() => toggleDropdown(subCategory?._id)}
                                                    className="p-1 hover:bg-gray-100 rounded-full">
                                                    <MoreVertical size={16} />
                                                </button>
                                                {showDropdown === subCategory?._id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 z-10">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => openEditSubCategoryModal(subCategory)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => openDeleteSubCategoryConfirmation(subCategory)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100 flex items-center gap-2">
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="flex justify-end">
                    <div className="flex items-center">
                        <div className="mr-4 flex justify-between">
                            <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                            <select
                                className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="flex items-center text-sm">
                            <span className="mr-4">Page {currentPage} of {totalPages}</span>
                            <div className="flex">
                                <button
                                    className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}>
                                    <ChevronsLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}>
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}>
                                    <ChevronRight size={16} />
                                </button>
                                <button
                                    className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}>
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add Question Modal */}
            {showAddSubCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Add Sub Category</h2>
                            <p className="text-gray-500 text-sm mb-6">Create a new sub category under selected category</p>
                            <div className="space-y-4">
                                <p className={`text-sm text-center mb-1 text-gray-800`}>Upload image</p>
                                <div className="mb-6 flex justify-center">
                                    <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                        {imagePreview ? (
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                        ) : (
                                            <Upload size={24} className="text-blue-600" />
                                        )}
                                        <input
                                            type='file'
                                            style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                            accept='image/*'
                                            onChange={handleImageChange}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={newTitle}
                                        onChange={(e) => setNewTitle(e.target.value)}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter title...."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => setShowAddSubCategoryModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAdd()}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Question Modal */}
            {showEditSubCategoryModal && editingSubCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Edit Sub Category</h2>
                            <p className="text-gray-500 text-sm mb-6">Update the Sub Category</p>
                            <div className="space-y-4">
                                <p className={`text-sm text-center mb-1 text-gray-800`}>Upload image</p>
                                <div className="mb-6 flex justify-center">
                                    <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                        {imagePreview ? (
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                        ) : (
                                            <Upload size={24} className="text-blue-600" />
                                        )}
                                        <input
                                            type='file'
                                            style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                            accept='image/*'
                                            onChange={handleImageChange}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={editingSubCategory?.title}
                                        onChange={(e) => setEditingSubCategory({ ...editingSubCategory, title: e.target.value })}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter title..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setEditingSubCategory(null);
                                    setShowEditSubCategoryModal(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleEdit}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Question Confirmation Modal */}
            {showDeleteSubCategoryConfirmation && deletingSubCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                                    <AlertTriangle size={20} />
                                </div>
                                <h2 className="text-xl font-semibold">Delete Sub Category</h2>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this sub category? This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setDeletingSubCategory(null);
                                    setShowDeleteSubCategoryConfirmation(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}